import api from '../baseAPIs/shopify';
import { uploadBlobToS3 } from '@/utils/s3Helper';
import { OptionSetDto, OptionSetCreateApi } from '@extTypes/options/optionSet';
import { LinkedOptionSet } from '@extTypes/enums/option-sets';

/**
 * Get option sets for the current user
 */
export const getOptionSets = async (
    searchString: string,
    from: number,
    count: number,
    forDesignTemplate = false,
    linkedOptionSets = LinkedOptionSet.All,
    store?: string,
    orderBy?: string,
    orderByDirection?: 'ASC' | 'DESC'
): Promise<OptionSetDto[]> => {
    const response = await api.get('optionsets', {
        params: {
            searchString: searchString,
            from: from,
            count: count,
            ...(store ? { shop: store } : {}),
            forDesignTemplate: forDesignTemplate,
            linkedOptionSets,
            orderBy: orderBy,
            orderByDirection: orderByDirection
        }
    });

    return response.data;
};

export const getProductsWithSet = async (
    setId: string
): Promise<
    {
        title: string;
        id: string;
        isDesign: boolean;
        imgSrc?: string;
        active: boolean;
        published: boolean;
    }[]
> => {
    const response = await api.get(`optionsets/${setId}/products`);
    return response.data;
};

export const getSetsCreatedFromTemplate = async (templateId: string): Promise<OptionSetDto[]> => {
    const response = await api.get(`optionsets/createdFromTemplate/${templateId}`);
    return response.data;
};

export const getOptionSetsCount = async (
    searchString: string,
    forDesignTemplate = false,
    linkedOptionSets = LinkedOptionSet.All,
    store?: string
): Promise<{
    count: number;
}> => {
    const response = await api.get('optionsets/count', {
        params: {
            searchString: searchString,
            shop: store,
            forDesignTemplate: forDesignTemplate,
            linkedOptionSets
        }
    });

    return response.data;
};

export const getOptionSet = async (setId: string, includeOptions: boolean): Promise<OptionSetDto> => {
    const response = await api.get('optionsets/private/' + setId, {
        params: {
            includeOptions: includeOptions
        }
    });

    return response.data;
};

export const addOptionSet = async (set: OptionSetCreateApi, forDesignTemplate = false): Promise<string> => {
    const response = await api.post('optionsets', set, {
        params: {
            forDesignTemplate: forDesignTemplate
        }
    });
    // Created set id
    return response.data.id;
};

export const saveOptionSet = async (set: OptionSetDto): Promise<number> => {
    const response = await api.put('optionsets', set);
    // Saved set id
    return response.data;
};

export const renameOptionSet = async (setId: string, title: string): Promise<void> => {
    await api.put(`optionsets/${setId}/title`, { title });
};

export const cloneOptionSet = async (set: OptionSetDto, forDesignTemplate = false): Promise<number> => {
    const response = await api.post(`optionsets/${set.id}`, set, { params: { forDesignTemplate } });
    // Cloned set id
    return response.data;
};

export const deleteSet = async (set: OptionSetDto): Promise<void> => {
    await api.delete('optionsets/' + set.id);
    return;
};

export const bulkDeleteOptionsSets = async (optionSetIds: string[]): Promise<void> => {
    await api.delete('optionsets/', { data: { setInternalIds: optionSetIds } });
};

export const regenerateSet = async (
    setId: string,
    productId?: string
): Promise<{
    setId: string;
    taskIdentifier: string;
}> => {
    const response = await api.post(`optionsets/regenerate/${setId}`, { productId });
    return response.data;
};

export const createSetFromTemplate = async (
    templateId: string,
    isManaged: boolean,
    title: string,
    productId?: string,
    storeUrl?: string
): Promise<{
    setId: string;
    taskIdentifier: string;
}> => {
    const response = await api.post('optionsets/createSetFromTemplate/' + templateId, {
        isManaged,
        title,
        productId,
        storeUrl
    });
    return response.data;
};

export const getProgress = async (progressIdentifier: string): Promise<number | null> => {
    const response = await api.get('/optionsets/progress/' + progressIdentifier);
    return response.data.progress;
};

export const getProgressInfo = async (
    productId: string
): Promise<{ progress: number; identifier: string; templateId: string; error: boolean } | null> => {
    const response = await api.get(`/optionsets/progress/info/${productId}`);
    return response.data;
};

const TASK_POLLING_MS = 3000;

/**
 * Waits for a task processed by the sets service to finish. Resolves with whether it failed (completely or partially),
 *  rejects when it doesn't finish in time.
 */
const waitForTask = async (taskIdentifier: string, timeoutMs: number): Promise<{ error: boolean }> => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, TASK_POLLING_MS));
        // A failed poll is retried on the next tick
        const response = await api.get('/optionsets/progress/' + taskIdentifier).catch(() => null);
        if (response?.data.progress === 100) return { error: Boolean(response.data.error) };
    }
    throw new Error(`Task ${taskIdentifier} did not finish in time`);
};

/**
 * Exports the given sets (they can belong to different stores and platforms) to a zip. Resolves with its URL once
 *  generated, sets that no longer exist are skipped.
 */
export const exportOptionSets = async (setIds: string[]): Promise<string> => {
    const response = await api.post<{ taskIdentifier: string; exportUrl: string }>('optionsets/export', { setIds });
    const { error } = await waitForTask(response.data.taskIdentifier, 1000 * 60 * 15);
    if (error) throw new Error('Error exporting the option sets');
    return response.data.exportUrl;
};

/**
 * Imports the option sets of a zip (current format or one from the old export service) into a store of any platform,
 *  the current one when storeUrl is omitted. Resolves with whether every set was imported.
 */
export const importOptionSets = async (file: File, storeUrl?: string): Promise<boolean> => {
    const { data: upload } = await api.get<{ s3Key: string; signedUrl: string }>('optionsets/import/url');
    await uploadBlobToS3(file, upload.signedUrl, 'application/zip');

    const response = await api.post<{ taskIdentifier: string }>('optionsets/import', {
        s3Key: upload.s3Key,
        storeUrl
    });
    const { error } = await waitForTask(response.data.taskIdentifier, 1000 * 60 * 30);
    return !error;
};

export const hasValueNameAndTooltipSwatches = async (): Promise<{
    hasValueNameAndTooltipSwatches: boolean;
}> => {
    const response = await api.get('optionsets/valueTooltip');
    return response.data;
};

export const generateOptionLayer = async (
    layer: {
        uuid: string;
    },
    template: string,
    setId?: string,
    forDesignTemplate?: boolean
): Promise<{
    setId: string;
    taskIdentifier: string;
}> => {
    const response = await api.post(`optionsets/optionGenerateLayer/${setId}`, {
        template,
        layer: layer.uuid,
        forDesignTemplate
    });
    return response.data;
};
